import React, { useState, useRef, useEffect, Fragment } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  flexRender,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { ReactComponent as ThreeDotsVertical } from "../../assets/images/threeDotVertical.svg";
import { ReactComponent as SubTaskIcon } from "../../assets/images/subTaskIcon.svg";
import ToolTipComponent from "./ToolTipComponent";
import SearchIcon from "../../assets/images/icons/SearchIcon";
import FilterIcon from "../../assets/images/icons/FilterIcon";
import DeleteIcon from "../../assets/images/icons/deleteIcon";
import moment from "moment";
import FloatingElementPortal from "./FloatingElementPortal";
import BasicSelect from "../Task/BasicSelect";
import HighlightedParagraph from "./HighlightedParagraph";
// import { ReactComponent as NoFile } from "../../assets/images/NoFile.svg";
import UserProfliePodModelDetails from "./UserProfliePodModelDetails";
import Loader from "./Loader";

const fuzzyFilter = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info (addMeta may not exist in plain JavaScript)
  if (addMeta) {
    addMeta({
      itemRank,
    });
  }

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

const ReactTableComponent = ({
  tablename,
  headers = [
    {
      accessor_key: "id",
      label: "Id",
      callingFunction: () => console.log("Called Column Function"),
      callingFunctionMore: () => console.log("Called Column Functionmore"),
      state: () => console.log("about status"),
    },
    { accessor_key: "name", label: "Name" },
    { accessor_key: "date", label: "Date", callingFunction: () => console.log("Called Column Function") },
  ],
  body = [{ id: 1, name: "test", date: "2023-01-01" }],
  isDeletable,
  actionField,
  actionColumn,
  noActionLabel,
  closeActionField,
  onlySortable,
  disableSearchIcon,
  disableFilterIcon,
  customerTablePagination,
  onRowClick = () => null,
  initialFilter,
  columnOrder,
  BoolString,
  isRightContextMenuActive = false,
  rightContextMenuOptions,
  noDataFound = "No Data Found",
  subChildDisplay,
  resetPageIndex = true,
  isProfilePodToolTip = false,
  refetchprofilePodTable = () => {},
  isLoading = false,
  columnTypes = {},
  setColumnTypes = () => {},
}) => {
  const [sorting, setSorting] = React.useState([]);
  const [grouping, setGrouping] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [subtaskExpanded, setSubtaskExpanded] = useState({});
  const [isSearchActive, setIsSearchActive] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFilterHovered, setIsFilterHovered] = useState(false);
  const [showBoolList, setShowBoolList] = useState(false);
  const child1Ref = useRef(null);
  const tableRef = useRef(null);
  const scrollbarWrapperRef = useRef(null);
  const paginationRef = useRef(null);
  const dropdownRef = useRef(null);
  const [selectedType, setSelectedType] = useState({});
  const toggleSearchIcon = () => {
    setIsSearchActive((prevState) => !prevState);
  };

  const HEADER_COLUMN_DATATYPE_OPTION = [
    { label: "String", value: "string" },
    { label: "Number", value: "number" },
  ];

  const cellRef = useRef(null);

  useEffect(() => {
    if (!isSearchActive) {
      setIsHovered(false);
    }
  }, [isSearchActive]);
  const toggleFilterActive = () => {
    setIsFilterActive((prevState) => !prevState);
  };

  useEffect(() => {
    if (!isFilterActive) {
      setIsFilterHovered(false);
    }
  });
  const handleScroll = (direction) => {
    if (scrollbarWrapperRef.current) {
      const scrollAmount = 100; // Adjust this value to control the scroll speed
      const currentScroll = scrollbarWrapperRef.current.scrollLeft;

      // Correct scrolling logic based on direction
      if (direction === "next") {
        // Scroll right (increase scrollLeft)
        scrollbarWrapperRef.current.scrollLeft = currentScroll + scrollAmount;
      } else if (direction === "previous") {
        // Scroll left (decrease scrollLeft), but don't go below 0
        scrollbarWrapperRef.current.scrollLeft = Math.max(currentScroll - scrollAmount, 0);
      }
    }
  };

  const [scrollbarWidth, setScrollbarWidth] = useState("100%");

  const pageNumbers = () => {
    const totalPages = table.getPageCount();
    const currentPage = table.getState().pagination.pageIndex + 1; // TanStack uses 0-based index

    const pages = [];

    if (totalPages <= 6) {
      // If total pages are 6 or fewer, show all
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1); // Always show the first page

      if (currentPage > 4) {
        pages.push("..."); // Ellipsis for pages before the current range
      }

      // Show pages around the current page
      for (let i = Math.max(2, currentPage - 2); i <= Math.min(totalPages - 1, currentPage + 2); i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 3) {
        pages.push("..."); // Ellipsis for pages after the current range
      }

      pages.push(totalPages); // Always show the last page
    }

    return pages;
  };

  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);

  // const pageSizes = [25, 50, 100];
  const pageSizes = [
    { label: "25", value: 25 },
    { label: "50", value: 50 },
    { label: "100", value: 100 },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (open && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      if (rect.bottom + 100 > windowHeight) {
        setDropUp(true);
      } else {
        setDropUp(false);
      }
    }
  }, [open]);

  const toggleDropdown = (headerId) => {
    setOpenDropdownId((prevId) => (prevId === headerId ? null : headerId));
  };
  const handleSort = (header, order) => {
    table.setSorting([
      {
        id: header.column.id, // Column ID to sort
        desc: order === "desc", // true for descending, false for ascending
      },
    ]);
  };

  const columns = headers?.map((header) => {
    return {
      accessorKey: header.accessor_key,

      header: header.label,
      cell: (info) => {
        if (header.boolColumn) console.log("boolColumn", Boolean(null));
        const onClickHandler = header.callingFunction
          ? () => header.callingFunction(info.row.original, header.accessor_key)
          : undefined;

        const toolTipHandler = header.toolTipHandler
          ? () => header.toolTipHandler(info.row.original, header.accessor_key)
          : undefined;

        const content = (
          <p
            ref={cellRef}
            onClick={(e) => {
              if (header.boolColumn) {
                setShowBoolList(info.row.id);
              } else {
                if (onClickHandler) {
                  onClickHandler();
                  header.StopPropagation ? e.stopPropagation() : e.preventDefault();
                }
              }
            }}
            className={`${
              header.boolColumn
                ? Boolean(info.getValue())
                  ? "text-[#3DB22A]"
                  : "text-[#EF4444]"
                : Boolean(onClickHandler)
                  ? `text-[#1794BA] cursor-pointer ${header.titleCase && "font-500"}`
                  : `text-[#2C495D] ${header.titleCase && "font-600"} `
            }`}
          >
            {header.boolColumn && showBoolList === info.row.id && (
              <FloatingElementPortal
                open={showBoolList === info.row.id}
                onClose={() => setShowBoolList("")}
                containerRef={cellRef}
                mode="vertical"
                xMargin={-10}
              >
                <div className="flex items-center flex-col bg-white px-2 py-1  border rounded-lg shadow-inner min-w-[150px] ">
                  <span
                    className="text-[#3DB22A] w-full bg-white hover:bg-gray-200 cursor-pointer rounded p-1 px-4"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowBoolList("");
                      if (!Boolean(info.getValue())) onClickHandler();
                    }}
                  >
                    <i class="bi bi-check-circle-fill mr-2" />
                    {header.boolState?.trueState}
                  </span>
                  <span
                    className="text-[#EF4444] w-full bg-white hover:bg-gray-200 cursor-pointer rounded p-1 px-4"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowBoolList("");
                      if (Boolean(info.getValue())) onClickHandler();
                    }}
                  >
                    <i class="bi bi-x-circle-fill mr-2" />
                    {header.boolState?.falseState}
                  </span>
                </div>
              </FloatingElementPortal>
            )}

            {header.boolColumn &&
              (Boolean(info.getValue()) ? (
                <i className="bi bi-check-circle-fill mr-2" />
              ) : (
                <i className="bi bi-x-circle-fill mr-2" />
              ))}
            {header.boolColumn ? (
              Boolean(info.getValue()) ? (
                header.boolState?.trueState
              ) : (
                header.boolState?.falseState
              )
            ) : (
              <HighlightedParagraph paragraph={info?.getValue()} searchTerm={globalFilter} />
            )}
          </p>
        );

        return header.tooltip ? (
          isProfilePodToolTip ? (
            <UserProfliePodModelDetails
              userData={info.row.original}
              headerkey={header?.accessor_key}
              refetchprofilePodTable={refetchprofilePodTable}
            />
          ) : (
            <ToolTipComponent
              text={
                <header.tooltip data={info.row.original} handleClick={toolTipHandler} headerKey={header.accessor_key} />
              }
            >
              {header.dateColumn
                ? moment(info.getValue()).format(header.dateColumn)
                : header.selfContent
                  ? header.selfContent(info.row.original, header.accessor_key)
                  : content}
            </ToolTipComponent>
          )
        ) : header.dateColumn ? (
          moment(info.getValue()).format(header.dateColumn)
        ) : header.selfContent ? (
          header.selfContent(info.row.original, header.accessor_key)
        ) : (
          content
        );
      },
    };
  });

  // Check if isDeletable is present
  if (isDeletable) {
    columns.push({
      id: "delete",
      header: () => <div className="text-right"></div>,
      cell: (info) => (
        <button
          onClick={() => isDeletable(info.row.original)} // Call the function passed in props
          className=""
        >
          <DeleteIcon color={"red"} size={18} />
        </button>
      ),
    });
  }

  if (actionColumn) {
    columns.push({
      id: "action",
      header: () => !noActionLabel && <div className="">Action</div>,
      // cell: (info) => actionColumn,
      cell: (info) => (typeof actionColumn === "function" ? actionColumn(info.row.original) : actionColumn),
    });
  }

  const table = useReactTable({
    data: body || [], // Use data from the hook or an empty array
    columns,
    // getSubRows: (row) => row?.sub_tasks || [],
    //getSubRows,
    filterFns: {
      fuzzy: fuzzyFilter, //define as a filter function that can be used in column definitions
    },
    initialState: {
      pagination: {
        pageSize: 25, // Set your desired default page size here
      },
    },
    state: {
      sorting,
      grouping,
      columnFilters,
      globalFilter,

      columnVisibility,
      // rowSelection,
      columnOrder,
    },
    // state: {
    //   grouping,
    // },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "fuzzy", //apply fuzzy filter to the global filter (most common use case for fuzzy filter)
    onColumnFiltersChange: setColumnFilters,
    onGroupingChange: setGrouping,
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    autoResetPageIndex: resetPageIndex,

    debugTable: true,
    //groupedColumnMode:false
    // onSortingChange: setSorting,
    // onGroupingChange: setGrouping,
    // onColumnVisibilityChange: setColumnVisibility,
    // onRowSelectionChange: setRowSelection,
    // columnOrder,
    // getCoreRowModel: getCoreRowModel(), // Handles rows without pagination
    // // getSortedRowModel: getSortedRowModel(), // Sorting rows
    // getExpandedRowModel: getExpandedRowModel(),
    // getGroupedRowModel: getGroupedRowModel(), // Grouping rows
    //getSubRows: (row) => row.sub_tasks, // Link sub-tasks to subRows
    // onExpandedChange: setExpanded,
  });

  useEffect(() => {
    const child1 = child1Ref.current;
    const scrollbarWrapper = scrollbarWrapperRef.current;
    if (child1 && scrollbarWrapper) {
      const handleScroll = () => {
        if (child1.scrollLeft !== scrollbarWrapper.scrollLeft) {
          child1.scrollLeft = scrollbarWrapper.scrollLeft;
        }
      };

      const handleChild1Scroll = () => {
        if (scrollbarWrapper.scrollLeft !== child1.scrollLeft) {
          scrollbarWrapper.scrollLeft = child1.scrollLeft;
        }
      };

      scrollbarWrapper.addEventListener("scroll", handleScroll);
      child1.addEventListener("scroll", handleChild1Scroll);
      // Cleanup event listeners on component unmount
      // return () => {
      //     scrollbarWrapper.removeEventListener('scroll', handleScroll);
      //     child1.removeEventListener('scroll', handleChild1Scroll);
      // };
    }
  }, [table.getRowModel().rows]);

  useEffect(() => {
    // Calculate the total width of the table
    if (tableRef.current) {
      const tableWidth = tableRef.current.scrollWidth;
      const containerWidth = child1Ref.current.clientWidth;

      if (tableWidth > containerWidth + 20) {
        setScrollbarWidth(`${tableWidth}px`);
      } else {
        setScrollbarWidth("0px");
      }

      // setScrollbarWidth(`${tableWidth}px`);
    }
  }, [body, table.getRowModel().rows]);

  const handleTypeChange = (e, columnId) => {
    setColumnTypes((prevOb) => ({
      ...prevOb,
      [columnId]: e.target.value,
    }));
  };

  return (
    <div className="flex h-full flex-col border-[2px] border-[#EBF2F6] rounded p-2">
      <div className="flex justify-between m-1 items-center ">
        <div className="flex justify-center items-center">
          {subChildDisplay}
          <h4 className="font-medium text-16 text-203C50 capitalize">{tablename}</h4>
        </div>
        {/* <div className="flex justify-between  gap-2 me-1">
          <div className="flex items-center gap-1 h-full">
            {actionField && actionField}
            {isSearchActive && (
              <div>
                <DebouncedInput
                  value={globalFilter ?? ""}
                  onChange={(value) => setGlobalFilter(String(value))}
                  className="p-5 font-lg shadow border border-block "
                  placeholder="Search"
                />
              </div>
            )}
            {!disableSearchIcon && (
              <div className="border border-[#EBF2F6] rounded-md hover:bg-[#CECECE4D] hover:border-transparent">
                <span onClick={toggleSearchIcon}>
                  <SearchIcon isHovered={isHovered} isActive={isSearchActive} />
                </span>
              </div>
            )}

            {!disableFilterIcon && (
              <div>
                {" "}
                <span onClick={toggleFilterActive}>
                  {" "}
                  <FilterIcon isHovered={isHovered} isActive={isFilterActive} />
                </span>
              </div>
            )}
          </div>
        </div> */}
      </div>
      <div className="overflow-x-auto grow min-h-0 w-full flex flex-col justify-between">
        <div ref={child1Ref} className=" bg-white overflow-y-auto grow min-h-0 scrollbar-thin">
          <table
            ref={tableRef}
            className="fullscreen-table min-h-full min-w-full border-collapse border border-gray-300 text-[13px] text-center font-poppins table-fixed text-2C495D overflow-auto"
          >
            <thead className="text-[#1d4469] capitalize sticky bg-white border">
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <tr key={headerGroup.id} className={`group `}>
                    {headerGroup?.headers.map((header) => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          //className="border border-gray-300 p-2 text-left cursor-pointer relative whitespace-nowrap font-medium"
                          //className="relative pr-2 text-left  border whitespace-nowrap  text-ellipsis border-x-0 min-w-fit max-w-[20vw]"
                          //style={{ width: "auto", minWidth: "2vw" }}
                          className={`relative pr-2 text-left border whitespace-nowrap text-ellipsis border-r-2 bg-[#f3efef] min-w-fit ${
                            header.id === "delete" ? "w-[40px] max-w-[40px]" : "max-w-[20vw]"
                          }`}
                        >
                          {header.isPlaceholder ? null : (
                            <>
                              <div
                                className="flex justify-between gap-3 ms-4 p-2 min-w-[8vw]"
                                //className={
                                // header.column.getCanSort() ? "cursor-pointer select-none" : ""
                                //}
                                //onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                                // title={
                                //   header.column.getCanSort()
                                //     ? header.column.getNextSortingOrder() === "asc"
                                //       ? "Sort ascending"
                                //       : header.column.getNextSortingOrder() === "desc"
                                //       ? "Sort descending"
                                //       : "Clear sort"
                                //     : undefined
                                // }
                              >
                                {/* Grouping Button */}
                                {/* {header.column.getCanGroup() ? (
                                    <button
                                      {...{
                                        onClick: header.column.getToggleGroupingHandler(),
                                        style: {
                                          cursor: "pointer",
                                        },
                                      }}
                                    >
                                      {header.column.getIsGrouped()
                                        ? `🛑(${header.column.getGroupedIndex()}) `
                                        : `👊 `}
                                    </button>
                                  ) : null} */}

                                {/* Column Header */}
                                <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                                {/* <div className="relative">
                                  {header.id !== "delete" && !["Manage List", "Dynamic List"].includes(tablename) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleDropdown(header.id);
                                      }}
                                    >
                                      <ThreeDotsVertical />
                                    </button>
                                  )}
                                </div> */}
                                {openDropdownId === header.id && (
                                  <div
                                    ref={dropdownRef}
                                    className="absolute right-[-55px] top-6 mt-2 w-40 bg-white border border-gray-300 shadow-lg rounded"
                                  >
                                    <ul>
                                      <li
                                        className="p-2 hover:bg-gray-100 cursor-pointer"
                                        onClick={() => {
                                          handleSort(header, "asc");
                                          setOpenDropdownId(null);
                                        }}
                                      >
                                        Sort by Increasing
                                      </li>
                                      <li
                                        className="p-2 hover:bg-gray-100 cursor-pointer"
                                        onClick={() => {
                                          handleSort(header, "desc");
                                          setOpenDropdownId(null);
                                        }}
                                      >
                                        Sort by Decreasing
                                      </li>
                                      <li
                                        className="p-2 hover:bg-gray-100 cursor-pointer"
                                        onClick={() => {
                                          header.column.getToggleGroupingHandler()();
                                          setOpenDropdownId(null);
                                        }}
                                      >
                                        <span className="block">
                                          {" "}
                                          {/* Ensure Group By/Stop Grouping By is on its own line */}
                                          {header.column.getIsGrouped() ? "Stop Grouping By" : "Group By"}
                                        </span>
                                        <span className="block">
                                          {" "}
                                          {/* Ensure column name is on the next line */}
                                          {flexRender(header.column.columnDef.header, header.getContext())}
                                        </span>
                                      </li>
                                    </ul>
                                  </div>
                                )}

                                {/* Sorting Indicators */}
                                {/* {{
                                    asc: " 🔼",
                                    desc: " 🔽",
                                  }[header.column.getIsSorted() || ""] || null} */}
                              </div>
                              {/*Filter Input Box */}
                              {header.column.getCanFilter() && isFilterActive ? (
                                <div>
                                  <Filter column={header.column} />
                                </div>
                              ) : null}
                            </>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            {/* { table.getRowModel().rows.length > 0 ? */}
            {body.length ? (
              <tbody className="border">
                {table.getHeaderGroups().map((headerGroup) => {
                  return (
                    <tr key={headerGroup.id} className={`group `}>
                      {headerGroup?.headers.map((header) => {
                        return (
                          <td>
                            <BasicSelect
                              label={"abc"}
                              options={HEADER_COLUMN_DATATYPE_OPTION}
                              selectedOptions={HEADER_COLUMN_DATATYPE_OPTION?.filter(
                                (option) => option?.label?.toLowerCase() === columnTypes[header?.id]?.toLowerCase()
                              )}
                              // selectedOptions={columnTypes[header.id]}
                              value={selectedType[header.id]}
                              onChange={(e) => handleTypeChange(e, header.id)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {table.getRowModel().rows.map((row, key) => {
                  return (
                    <Fragment key={key}>
                      <tr
                        onClick={() => onRowClick(row.original)}
                        key={row.id}
                        className={`border  tablerow border-E1E6EC cursor-pointer group`}
                      >
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              className={`whitespace-nowrap border-b   border-neutral-200 text-2C495D font-normal text-left text-[12px] py-[6px]`}
                              style={{
                                background: cell.getIsGrouped()
                                  ? ""
                                  : cell.getIsAggregated()
                                    ? ""
                                    : cell.getIsPlaceholder()
                                      ? ""
                                      : "white",
                                marginLeft: cell.getIsGrouped()
                                  ? ""
                                  : cell.getIsAggregated()
                                    ? ""
                                    : cell.getIsPlaceholder()
                                      ? "-30px"
                                      : "white",
                              }}
                              // onClick={
                              //   !cell.getIsGrouped() && !["subject", "activity"].includes(cell.column.id)
                              //     ? (e) => {
                              //         e.stopPropagation();
                              //         handleNavigate(null, row.original);
                              //       }
                              //     : undefined
                              // }
                            >
                              {cell.getIsGrouped() ? (
                                // If it's a grouped cell, add an expander and row count
                                <>
                                  <button
                                    {...{
                                      onClick: row.getToggleExpandedHandler(),
                                      style: {
                                        cursor: row.getCanExpand() ? "pointer" : "normal",
                                      },
                                    }}
                                  >
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                      <span>{row.getIsExpanded() ? "▼" : "▶"}</span>
                                      <span>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                                      <span>({row.subRows.length})</span>
                                    </div>
                                  </button>
                                </>
                              ) : cell.getIsAggregated() ? (
                                // If the cell is aggregated, use the Aggregated
                                // renderer for cell
                                flexRender(
                                  cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                                  cell.getContext()
                                )
                              ) : cell.getIsPlaceholder() ? null : ( // For cells with repeated values, render null
                                // Otherwise, just render the regular cell
                                <div className="ms-4 text-black group">
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      {expanded[row.id] && // Check if the row has sub-rows and is expanded
                        row.subRows.map((subRow) => (
                          <tr key={subRow?.id} className="border-t border-gray-300 bg-gray-100">
                            {subRow.getVisibleCells().map((subCell) => (
                              <td key={subCell.id} className="border border-gray-300 p-2 whitespace-nowrap pl-8">
                                {subCell.column.columnDef.cell({
                                  getValue: subCell.getValue,
                                  row: subCell.row,
                                })}
                              </td>
                            ))}
                          </tr>
                        ))}
                      {subtaskExpanded[row.id] &&
                        row.original.sub_tasks.map((subtask, subtaskIndex) => {
                          return (
                            <tr key={`subtask-${row.id}-${subtaskIndex}`} className="bg-gray-100 cursor-pointer">
                              {row.getVisibleCells().map((cell) => {
                                // Create a context for the subtask using the existing cell context
                                const subtaskContext = {
                                  ...cell.getContext(),
                                  row: {
                                    ...cell.getContext().row,
                                    original: subtask, // Replace original data with subtask data
                                  },
                                };
                                const isSubjectColumn = cell.column.id === "subject";
                                const marginClass = isSubjectColumn ? "ms-12" : "ms-5";

                                return (
                                  <td
                                    key={cell.id}
                                    // onClick={(e) => handleNavigate(null, row.original)}
                                  >
                                    <div
                                      className={`${marginClass} text-black text-[12px] text-left flex items-center`}
                                    >
                                      {isSubjectColumn && (
                                        <div className="flex items-center justify-center bg-white rounded-10px border w-[21px] h-[21px] mr-1">
                                          <SubTaskIcon className="" />
                                        </div>
                                      )}
                                      <div className="flex-1">
                                        {flexRender(cell.column.columnDef.cell, subtaskContext)}
                                      </div>
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}

                      {row.getIsExpanded() &&
                        row.subRows.length > 0 &&
                        row.subRows.map((subRow) => (
                          <tr key={subRow.id} className="border tablerow border-E1E6EC ">
                            {subRow.getVisibleCells().map((subCell) => (
                              <td
                                key={subCell.id}
                                className={`whitespace-nowrap border-b    border-neutral-200 text-2C495D font-normal text-left text-[12px] py-0.5`}
                              >
                                <div className="ms-5 text-black text-[12px]">
                                  {flexRender(subCell.column.columnDef.cell, subCell.getContext())}
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}

                      {/* {row.getIsExpanded() && row.subRows.length > 0 && (
          row.subRows.map((subRow) => (
            <tr key={subRow.id} className="border tablerow border-E1E6EC">
              {subRow.getVisibleCells().map((subtaskCell) => (
                <td key={subtaskCell.id} className="border-b px-2 py-1 bg-gray-100">
                  {flexRender(subtaskCell.column.columnDef.cell, subtaskCell.getContext())}
                </td>
              ))}
            </tr>
          ))
        )} */}
                    </Fragment>
                  );
                })}
              </tbody>
            ) : isLoading ? (
              <>
                <tbody>
                  <tr>
                    <td colSpan={"10%"} className="bg-white text-center py-4">
                      <Loader isOpen={isLoading} />
                    </td>
                  </tr>
                </tbody>
              </>
            ) : (
              <tbody>
                {" "}
                <tr>
                  {/* <td></td> */}
                  <td colSpan={"10%"} className="bg-white text-center py-4">
                    {noDataFound}
                  </td>
                  {/* <td></td> */}
                </tr>
              </tbody>
            )}
          </table>
        </div>
        {/* <div className="flex items-center justify-between bg-white gap-2 px-4 py-1.5 border border-[#D2DAE1] rounded-b-lg "> */}
        {/* <div ref={paginationRef} className="flex min-w-fit auto flex-grow gap-3"> */}
        {/* <div className="relative w-fit" ref={dropdownRef}>
              <BasicSelect
                options={pageSizes || []}
                place
                name="pageSize"
                selectedOptions={pageSizes?.filter((option) => option?.value === table.getState().pagination.pageSize)}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
                onClick={() => {
                  table.setPageSize(pageSizes[0]?.value);
                }}
                type={"select"}
                size={"medium"}
              />
            </div> */}

        {/* Pagination Controls */}
        {/* <div className="bg-white min-w-fit ">
              <ul className="globalpagination  border rounded border-[#DDE1E3] text-sm font-medium text-[#535252]  py-[0.16875rem]  items-center justify-center flex gap-1">
   
                <li>
                  <button
                    className="hover:border-[#D4D5D6] px-1 py-0.5 rounded text-[#535252]"
                    onClick={() => {
                      table.setPageIndex(0);
                    }}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <i className="bi bi-chevron-double-left   text-sm font-medium text-[#535252]"></i>
                  </button>
                </li>

                       <li>
                  <button
                    className="hover:border-[#D4D5D6] px-1 py-0.5 rounded"
                    onClick={() => {
                      table.previousPage();
                   
                    }}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <i className="bi bi-chevron-left font-medium"></i>
                  </button>
                </li>

          
                {pageNumbers().map((page, index) => (
                  <li
                    key={index}
                    className={`${table.getState().pagination.pageIndex === page - 1
                      ? " border-383838 border  hover:border-[#D4D5D6]  rounded items-center"
                      : "" 
                      } rounded  flex items-center justify-center`}
                  >
                    {page === "..." ? (
                      <span className=" rounded ">...</span>
                    ) : (
                      <button
                        className={`text-sm font-medium  ${table.getState().pagination.pageIndex === page - 1
                          ? " border-383838 px-2"
                          : "hover:border-[#D4D5D6]  rounded items-center  py-0.5 px-2 " 
                          }`}
                        style={{ color: "#535252" }}
                        onClick={() => {
                          table.setPageIndex(page - 1);
                        }}
                      >
                        {page}
                      </button>
                    )}
                  </li>
                ))}

                <li>
                  <button
                    className="rounded hover:border-[#D4D5D6] px-1 py-0.5"
                    onClick={() => {
                      table.nextPage();
                    }}
                    disabled={!table.getCanNextPage()}
                  >
                    <i className="bi bi-chevron-right  font-medium"></i>
                  </button>
                </li>

                <li>
                  <button
                    className="rounded hover:border-[#D4D5D6] px-1 py-0.5"
                    onClick={() => {
                      table.setPageIndex(table.getPageCount() - 1);
                    }}
                    disabled={!table.getCanNextPage()}
                  >
                    <i className="bi bi-chevron-double-right  font-medium"></i>
                  </button>
                </li>
              </ul>
            </div> */}

        {/* Total Records */}
        {/* <div className="flex items-center">
              <p className=" bg-none  bg-white font-medium text-sm  text-535252 min-w-fit flex items-center gap-1">
                <span>Count </span>
                <span className="mt-[-1px]"> :: </span>
                <span> {table.getFilteredRowModel().rows.length}</span>
              </p>
            </div> */}
        {/* </div> */}
        {/* <div className={` ${scrollbarWidth === "0px" ? "hidden" : ""}`}>
            <ThreeDotsVertical />
          </div> */}
        {/* <div className="flex justify-between items-center w-1/2 ml-1/4">
            <button
              className={`z-50 mr-[-20px] bg-[#5d5f60] bg-opacity-100 text-white px-2 h-[15px] text-sm rounded-l-full flex items-center justify-center ${scrollbarWidth === "0px" ? "hidden" : ""
                }`}
              onClick={() => handleScroll("previous")}
            >
              <span className="" style={{ fontSize: "7.5px" }}>
                &#9664;
              </span>
            </button>
            <div
              className={`flex-grow overflow-x-auto overflow-y-hidden scrollbar-thin customscrollbar1 h-[15px] w-[calc(100%-${scrollbarWidth})] `}
              style={{}}
              ref={scrollbarWrapperRef}
            >
              <div
                style={{
                  width: `calc(${scrollbarWidth} - ${paginationRef?.current?.clientWidth}px)`,
                  height: "1px",
                }}
              ></div>
            </div>
            <button
              className={`z-50 ml-[-20px]  bg-[#5d5f60] bg-opacity-100 text-white px-2 h-[15px] text-sm rounded-r-full flex items-center justify-center ${scrollbarWidth === "0px" ? "hidden" : ""
                }`}
              onClick={() => handleScroll("next")}
            >
              <span className="" style={{ fontSize: "7.5px" }}>
                &#9654;
              </span>
            </button>
          </div> */}
        {/* </div> */}
      </div>
    </div>
  );
};
function Filter({ column }) {
  const columnFilterValue = column.getFilterValue();
  const { filterVariant } = column.columnDef.meta || {};

  return filterVariant === "range" ? (
    <div>
      <div className="flex space-x-2">
        {/* See faceted column filters example for min max values functionality */}
        <DebouncedInput
          type="number"
          value={(columnFilterValue && columnFilterValue[0]) || ""}
          onChange={(value) => column.setFilterValue((old) => [value, old ? old[1] : undefined])}
          placeholder={`Min`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          value={(columnFilterValue && columnFilterValue[1]) || ""}
          onChange={(value) => column.setFilterValue((old) => [old ? old[0] : undefined, value])}
          placeholder={`Max`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === "select" ? (
    <select
      onChange={(e) => column.setFilterValue(e.target.value)}
      value={columnFilterValue ? columnFilterValue.toString() : ""}
    >
      {/* See faceted column filters example for dynamic select options */}
      <option value="">All</option>
      <option value="complicated">complicated</option>
      <option value="relationship">relationship</option>
      <option value="single">single</option>
    </select>
  ) : (
    <input
      className="w-36 border shadow rounded placeholder:pl-1"
      onChange={(e) => {
        column.setFilterValue(e.target.value);
      }}
      placeholder={`Search...`}
      type="text"
      value={columnFilterValue || ""}
    />

    // <DebouncedInput
    //   className="w-36 border shadow rounded"
    //   onChange={value => column.setFilterValue(value)}
    //   placeholder={`Search...`}
    //   type="text"
    //   value={columnFilterValue || ''}
    // />
    // See faceted column filters example for datalist search suggestions
  );
}
function DebouncedInput({ value: initialValue, onChange, debounce = 200, ...props }) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue); // Sync the input value with the parent prop
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value); // Call the parent onChange handler after debounce
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce, onChange]); // Debounce the value change

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)} // Update internal value on change
      className="text-black text-xs border-none outline-none bg-white p-3"
    />
  );
}

export default ReactTableComponent;
